import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { IntegrationCodePiece } from '@/features/equipmentModels/types/equipmentModelIntegrationCode'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

type UseEquipmentModelIntegrationCodePiecesProps<T> = {
  integrationCodeId?: string
  filters?: {
    search?: string
    page?: number
  }
  mapper?: (data: any) => T
  enabled?: boolean
  refetchInterval?: number | false
}

export const useEquipmentModelIntegrationCodePieces = <T,>({
  integrationCodeId,
  filters: { search = '', page } = {},
  mapper = Mapper.default<T>(),
  enabled = true,
  refetchInterval = false,
}: UseEquipmentModelIntegrationCodePiecesProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: ['equipmentModelIntegrationCodes', 'pieces', integrationCodeId, search, page],
    enabled: enabled && !!integrationCodeId,
    queryFn: () =>
      privateAxios.get(`/maintenance/equipment_model_integration_codes/${integrationCodeId}/pieces`, {
        params: {
          search: search || undefined,
          page,
        },
      }),
    select: (response: { data: { data: IntegrationCodePiece[]; meta: MetaResponse } }) => ({
      data: mapper(response?.data?.data),
      meta: response?.data?.meta,
    }),
    placeholderData: keepPreviousData,
    refetchInterval,
  })

  return { data, meta, isPending, ...rest }
}
